/**
 * PremiumPaymentGateway
 *
 * Handles the payment checkout flow as a modal
 * Handles activation error
 *
 * If the selected product type is free then this will auto activate the listing.
 * If the selected product type is premium then the payment modal will launch.
 * If the selected product type is premium and within the expiration date then this will auto activate the listing.
 */
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isPast } from 'date-fns';

import { useQueryClient } from '@zg-rentals/react-query5';
import PropertiesActions from '../../../../actions/PropertiesActions';
import { useActivateListing } from '../../../../hooks/queries/useActivateListing';
import { setQueryFreemiumActiveProduct } from '../../../../hooks/queries/useGetPropertyDetailsOverview';
import { useSelector } from '../../../../selectors/useSelector';

import DispatchConstants from '../../../../constants/DispatchConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';
import ModularListingCSEvents from '../../../../model/ClickstreamEvent/ModularListingCSEvents';
import PayAndPublishCSEvents from '../../../../model/ClickstreamEvent/PayAndPublishCSEvents';
import PostActivationModalCSEvents from '../../../../model/ClickstreamEvent/PostActivationModalCSEvents';

import FreemiumErrorModal from '../../../Modal/FreemiumErrorModal';
import PaymentModal from '../../../Modal/PaymentModal';
import PremiumMidfunnelSuccessModal from '../../../Modal/PremiumMidfunnelSuccessModal';

import { trackGtmEvent } from '../../../../../client/analytics';
import { ZRM_GTM_EVENTS } from '../../../../../client/analytics/gtmEvents';
import { PaymentGatewayContext } from '../../context';

import type { FreemiumChargeResponseWebDto } from '@zg-rentals/rental-manager-env';
import type { UserPropertyActivateResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ActiveProductProps } from '../../../../model/Freemium';
import type { FreemiumDatarouterError } from '../../../Modal/FreemiumErrorModal';

type Props = {
  displayPaymentGateway?: boolean;
  updateDisplayPaymentGateway?: ({
    displayPaymentGateway,
    isPremiumMidfunnelPayment,
    displayContext,
  }: {
    displayPaymentGateway: boolean;
    isPremiumMidfunnelPayment?: boolean;
    displayContext?: string;
  }) => void;
  onFreemiumResponse?: (response: Partial<FreemiumChargeResponseWebDto>) => void;
};

const MODAL_SWAP_DELAY = 100;

const PremiumPaymentGateway = ({
  // use value from prop until all PaymentGateway instances are launched from App
  // if not launched from App, displayPaymentGateway in pgState will be out of sync
  displayPaymentGateway = false,
  updateDisplayPaymentGateway,
  onFreemiumResponse,
}: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    pgState: { displayContext, isPremiumMidfunnelPayment },
  } = useContext(PaymentGatewayContext);

  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [publishError, setPublishError] = useState<{
    display: boolean;
    error?: FreemiumDatarouterError;
    onClose?: () => void;
  }>({
    display: false,
  });
  const [displayMidfunnelSuccessModal, setDisplayMidfunnelSuccessModal] = useState(false);

  const { mutate: activate } = useActivateListing();

  const {
    active,
    listingAlias,
    freemium,
    freemium: {
      activeProduct,
      activeProduct: { type: activeProductType, expirationTimestamp: freemiumActiveProductTimestamp },
      products,
      isInBuildingGroup = false,
    },
    selectedProductType,
  } = currentProperty;

  const isActiveProductExpired = isPast(new Date(freemiumActiveProductTimestamp));
  const isActiveProductPremiumAndNotExpired =
    activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;
  const foundIndex = products.findIndex((product: ActiveProductProps) => product.type === selectedProductType);
  const selectedProduct = products[foundIndex];
  const { type } = selectedProduct;

  useEffect(() => {
    if (displayPaymentGateway) {
      if (shouldAutoActivate() && !isPremiumMidfunnelPayment) {
        activateListing();
        return;
      }

      if (isActiveProductPremiumAndNotExpired) {
        return;
      }

      setDisplayPaymentModal(true);
    } else {
      setDisplayPaymentModal(false);
    }
  }, [displayPaymentGateway]);

  const getUpdatedActiveProduct = ({
    activateResponse,
    freemiumChargeResponse,
  }: {
    activateResponse?: UserPropertyActivateResponseWebDto;
    freemiumChargeResponse?: FreemiumChargeResponseWebDto;
  }) => {
    // we can not downgrade the active product
    // use active product information is not activating from the charge endpoint
    const tempProduct = freemiumChargeResponse ? selectedProduct : activeProduct;
    const { price, duration, type } = tempProduct;

    const computedExpirationDateTimestamp = freemiumChargeResponse
      ? freemiumChargeResponse?.expirationMs
      : activateResponse?.expirationMs;

    const response = {
      ...activeProduct,
      daysExtended: activateResponse?.daysExtended,
      expirationTimestamp: computedExpirationDateTimestamp,
      type,
      price,
      duration,
    };

    return response;
  };

  const shouldAutoActivate = () => {
    return (type === PRODUCT_TYPE_NAME_TO_ID.FREE || isActiveProductPremiumAndNotExpired) && !active;
  };

  // TODO: decouple activation from payment gateway
  // will need to create a post activation analytics util function
  const activateListing = (freemiumChargeResponse?: FreemiumChargeResponseWebDto) => {
    return activate(
      { getParams: { listingAlias } },
      {
        onSuccess: (activateResponse) => {
          const updatedActiveProduct = getUpdatedActiveProduct({ activateResponse, freemiumChargeResponse });
          const productOverviewDataFromCache = queryClient.getQueryData(['getPropertyDetailsOverview', listingAlias]);

          syncWithCurrentProperty({ updatedActiveProduct });
          dispatch(
            PropertiesActions.updatePostActivationState({
              daysExtended: updatedActiveProduct?.daysExtended,
              displayPostActivationModal: true,
            }),
          );
          if (productOverviewDataFromCache) {
            syncWithQueryCache({ updatedActiveProduct });
          }

          const csEventParams = {
            currentProperty: {
              ...currentProperty,
              freemium: {
                ...freemium,
                activeProduct: updatedActiveProduct,
              },
            },
            user,
          };

          PayAndPublishCSEvents.ActivateListing(csEventParams);

          if (freemiumChargeResponse) {
            // handlePremiumActivation csEvents
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PAY_PUBLISH });

            PostActivationModalCSEvents.CompletesPremiumUpsell(csEventParams);
          } else {
            // handleFreeActivation csEvents
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });

            // always update display
            updateDisplayPaymentGateway?.({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          }

          onFreemiumResponse?.({
            expirationMs: activateResponse?.expirationMs,
            activeProductType: updatedActiveProduct?.type,
          });

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          handleActivationError(error as FreemiumDatarouterError);
        },
      },
    );
  };

  const syncWithCurrentProperty = ({
    updatedActiveProduct,
  }: {
    updatedActiveProduct: ActiveProductProps & { daysExtended: number };
  }) => {
    const { daysExtended, expirationTimestamp } = updatedActiveProduct;
    dispatch(
      PropertiesActions.updateCurrentProperty({
        ...currentProperty,
        active: isPremiumMidfunnelPayment ? false : true,
        daysExtended,
        expirationDateTimestamp: expirationTimestamp,
        freemium: {
          ...freemium,
          activeProduct: updatedActiveProduct,
        },
      }),
    );
  };

  const syncWithQueryCache = ({ updatedActiveProduct }: { updatedActiveProduct: ActiveProductProps }) => {
    setQueryFreemiumActiveProduct({
      config: {
        queryClient,
        listingAlias,
      },
      payload: {
        activeProduct: updatedActiveProduct,
      },
    });
  };

  const handleActivationError = (error: FreemiumDatarouterError) => {
    setPublishError({
      display: true,
      error,
      onClose: () => {
        updateDisplayPaymentGateway?.({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
        setPublishError({ display: false });
      },
    });
  };

  const handleFreemiumChargeSuccess = (response: FreemiumChargeResponseWebDto) => {
    if (response.isPaymentValid) {
      if (!isPremiumMidfunnelPayment && !active) {
        activateListing(response);

        ModularListingCSEvents.CompleteUpgradeToPremium({
          currentProperty,
          user,
          triggerSource: ModularListingCSEvents.TRIGGER_SOURCES.AT_ACTIVATION,
        });
      } else {
        setDisplayMidfunnelSuccessModal(true);
        setDisplayPaymentModal(false);

        const updatedActiveProduct = getUpdatedActiveProduct({
          freemiumChargeResponse: response,
        });

        const productOverviewDataFromCache = queryClient.getQueryData(['getPropertyDetailsOverview', listingAlias]);
        syncWithCurrentProperty({ updatedActiveProduct });
        if (productOverviewDataFromCache) {
          syncWithQueryCache({ updatedActiveProduct });
        }

        PostActivationModalCSEvents.CompletesPremiumUpsell({
          currentProperty: {
            ...currentProperty,
            freemium: {
              ...freemium,
              activeProduct: updatedActiveProduct,
            },
          },
          user,
        });

        if (active === false) {
          ModularListingCSEvents.CompleteUpgradeToPremium({
            currentProperty,
            user,
            triggerSource: ModularListingCSEvents.TRIGGER_SOURCES.PREACTIVATION,
          });
        }
      }

      // always update display
      updateDisplayPaymentGateway?.({ displayPaymentGateway: false, displayContext });
      dispatch({
        type: DispatchConstants.RESET_CREDIT_CARD_CAPTURE_POST_PAYMENT,
      });
    }

    onFreemiumResponse?.(response);
  };

  const updateSelectedProductType = (selectedProductType: number) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handleFreemiumErrorPublishForFreeClick = () => {
    setPublishError({ display: false });
    updateSelectedProductType(PRODUCT_TYPE_NAME_TO_ID.FREE);
    activateListing();
  };

  const handleFreemiumErrorTryAgainClick = () => {
    setPublishError({ display: false });

    setTimeout(() => {
      updateDisplayPaymentGateway?.({ displayPaymentGateway: true, isPremiumMidfunnelPayment });
    }, MODAL_SWAP_DELAY);
  };

  const handleFreemiumChargeError = (err: FreemiumDatarouterError) => {
    updateDisplayPaymentGateway?.({ displayPaymentGateway: false, isPremiumMidfunnelPayment });

    setTimeout(() => {
      setPublishError({
        display: true,
        error: err,
        onClose: () => {
          updateDisplayPaymentGateway?.({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          setPublishError({ display: false });
        },
      });
    }, MODAL_SWAP_DELAY);
  };

  return (
    <div data-testid="PremiumPaymentGateway">
      {publishError.display ? (
        <FreemiumErrorModal
          error={publishError?.error}
          onClose={publishError?.onClose || (() => {})}
          onTryAgainClick={handleFreemiumErrorTryAgainClick}
          onPublishForFreeClick={handleFreemiumErrorPublishForFreeClick}
        />
      ) : null}
      {displayPaymentModal && (
        <PaymentModal
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={handleFreemiumChargeError}
          listingAlias={listingAlias}
          isRenderedInPageView={false}
          onClose={() => {
            // always update display
            setDisplayPaymentModal(false);
            updateDisplayPaymentGateway?.({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          }}
          isInBuildingGroup={isInBuildingGroup}
        />
      )}
      {displayMidfunnelSuccessModal && (
        <PremiumMidfunnelSuccessModal onClose={() => setDisplayMidfunnelSuccessModal(false)} isActive={active} />
      )}
    </div>
  );
};

export default PremiumPaymentGateway;
